import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { MealPlanFoodItem } from 'src/pages/consumer/meal-plans/checkout/types';
import { useShareDish } from 'src/shared/hooks/useShareDish';
import { CloseButton } from 'web-common/src/shared/design-system/components/Button/CloseButton';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { DishModalContent } from './DishModalContent';

interface DishModalHeaderProps {
  foodItem: MealPlanFoodItem;
  onClose: () => void;
  closeButtonRef: React.RefObject<HTMLDivElement>;
  onHeaderRef?: (ref: HTMLDivElement | null) => void;
}

export const DishModalHeader: React.FC<DishModalHeaderProps> = ({ foodItem, onClose, closeButtonRef, onHeaderRef }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { share } = useShareDish(foodItem);

  useEffect(() => {
    const closeButton = closeButtonRef.current;
    if (!closeButton) {
      return undefined;
    }

    // Show header when close button is not visible (scrolled out of view)
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(closeButton);

    return () => {
      observer.disconnect();
    };
  }, [closeButtonRef]);

  return (
    <div
      ref={onHeaderRef}
      aria-hidden={!isVisible}
      className={cn(
        'absolute left-0 top-0 z-20 h-[45px] w-full border-b border-eggplant-50',
        'bg-milk py-3 transition-transform duration-200',
        {
          'pointer-events-none -translate-y-full': !isVisible,
        }
      )}>
      <DishModalContent className='flex items-start justify-between gap-6'>
        {/* Dish name with ellipsis */}
        <p className='flex-1 truncate text-body-600 text-eggplant'>{foodItem.name}</p>

        {/* Share and Close buttons */}
        <button onClick={share} className='flex items-center justify-center'>
          <Icon name='share' color={IconColor.RASPBERRY} size={16} />
        </button>

        <CloseButton onClick={onClose} />
      </DishModalContent>
    </div>
  );
};
